import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
import {
  AUDIT_LOG_CATEGORIES,
  AUDIT_LOG_LEVELS,
  clearAuditLogs,
  downloadAuditLogsCsv,
  getAuditLogSettings,
  getAuditLogs,
  saveAuditLogSettings
} from '../../services/auditLogs.service'
import './AdminAuditLogs.css'

const LEVEL_LABELS = {
  critical: 'Crítico',
  error: 'Error',
  warning: 'Warning',
  info: 'Info'
}

const CATEGORY_LABELS = {
  access: 'Accesos',
  auth: 'Autenticación',
  notifications: 'Notificaciones',
  payments: 'Pagos',
  orders: 'Pedidos',
  sales: 'Ventas',
  stock: 'Stock',
  system: 'Sistema'
}

const clone = (value) => JSON.parse(JSON.stringify(value || {}))

function AdminAuditLogs() {
  const [settings, setSettings] = useState(null)
  const [draft, setDraft] = useState(null)
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  const [levelFilter, setLevelFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [searchText, setSearchText] = useState('')
  const [expandedRows, setExpandedRows] = useState(() => new Set())
  const [clearing, setClearing] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)

  const loadData = async () => {
    try {
      setLoading(true)
      const [settingsData, logsData] = await Promise.all([
        getAuditLogSettings(),
        getAuditLogs({ limit: 300 })
      ])
      setSettings(settingsData)
      setDraft(clone(settingsData))
      setLogs(Array.isArray(logsData) ? logsData : [])
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'No se pudieron cargar los logs' })
      setTimeout(() => setNotification(null), 4000)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const filteredLogs = useMemo(() => {
    const term = searchText.trim().toLowerCase()
    return logs.filter((log) => {
      if (levelFilter !== 'all' && log.level !== levelFilter) return false
      if (categoryFilter !== 'all' && log.category !== categoryFilter) return false
      if (!term) return true
      const message = `${log.message || ''} ${log.action || ''} ${log.category || ''} ${log.level || ''}`
      return message.toLowerCase().includes(term)
    })
  }, [logs, levelFilter, categoryFilter, searchText])

  const toggleExpanded = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const updateDraftLevel = (level, value) => {
    setDraft((prev) => ({
      ...prev,
      levels: {
        ...(prev?.levels || {}),
        [level]: value
      }
    }))
  }

  const updateDraftCategory = (category, value) => {
    setDraft((prev) => ({
      ...prev,
      categories: {
        ...(prev?.categories || {}),
        [category]: value
      }
    }))
  }

  const handleSave = async () => {
    if (!draft || saving) return
    try {
      setSaving(true)
      const saved = await saveAuditLogSettings(draft)
      setSettings(saved)
      setDraft(clone(saved))
      setNotification({ type: 'success', message: 'Configuración guardada correctamente' })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'No se pudo guardar la configuración' })
      setTimeout(() => setNotification(null), 4000)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setDraft(clone(settings))
  }

  const handleExportCsv = () => {
    if (!filteredLogs.length) {
      setNotification({ type: 'error', message: 'No hay logs para exportar' })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    const date = new Date().toISOString().slice(0, 10)
    downloadAuditLogsCsv(filteredLogs, `audit-logs-${date}.csv`)
  }

  const handleClearLogs = async () => {
    if (clearing) return
    try {
      setClearing(true)
      const deleted = await clearAuditLogs()
      await loadData()
      setNotification({ type: 'success', message: `Logs eliminados: ${deleted}` })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'No se pudieron borrar los logs' })
      setTimeout(() => setNotification(null), 4000)
    } finally {
      setClearing(false)
    }
  }

  if (loading) {
    return (
      <div className="admin-audit-logs-page">
        <div className="audit-logs-loading">Cargando auditoría...</div>
      </div>
    )
  }

  return (
    <div className="admin-audit-logs-page">
      <div className="audit-logs-header">
        <div>
          <h2>Auditoría y Logs</h2>
          <p>Configura qué eventos se registran y revisa el historial completo</p>
        </div>
        <div className="audit-logs-actions">
          <Link to="/admin/configuraciones">
            <Button variant="outline">← Volver</Button>
          </Link>
          <Button variant="secondary" onClick={loadData}>
            Actualizar
          </Button>
          <Button variant="outline" onClick={handleExportCsv}>
            Descargar CSV
          </Button>
          <Button variant="danger" onClick={() => setConfirmClear(true)} disabled={clearing}>
            {clearing ? 'Borrando...' : 'Borrar logs'}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      <div className="audit-logs-section">
        <h3>Configuración de registro</h3>
        <div className="audit-logs-switch">
          <label>
            <input
              type="checkbox"
              checked={draft?.enabled !== false}
              onChange={(e) => setDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            Habilitar auditoría
          </label>
        </div>

        <div className="audit-logs-grid">
          <div className="audit-logs-group">
            <h4>Niveles</h4>
            {AUDIT_LOG_LEVELS.map((level) => (
              <label key={level} className="audit-logs-checkbox">
                <input
                  type="checkbox"
                  checked={draft?.levels?.[level] !== false}
                  onChange={(e) => updateDraftLevel(level, e.target.checked)}
                />
                {LEVEL_LABELS[level] || level}
              </label>
            ))}
          </div>
          <div className="audit-logs-group">
            <h4>Categorías</h4>
            {AUDIT_LOG_CATEGORIES.map((category) => (
              <label key={category} className="audit-logs-checkbox">
                <input
                  type="checkbox"
                  checked={draft?.categories?.[category] !== false}
                  onChange={(e) => updateDraftCategory(category, e.target.checked)}
                />
                {CATEGORY_LABELS[category] || category}
              </label>
            ))}
          </div>
        </div>

        <div className="audit-logs-actions-row">
          <Button variant="outline" onClick={handleReset}>
            Descartar cambios
          </Button>
        </div>
      </div>

      <div className="audit-logs-section">
        <h3>Visor de eventos</h3>
        <div className="audit-logs-filters">
          <div className="audit-logs-field">
            <label>Nivel</label>
            <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
              <option value="all">Todos</option>
              {AUDIT_LOG_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {LEVEL_LABELS[level] || level}
                </option>
              ))}
            </select>
          </div>
          <div className="audit-logs-field">
            <label>Categoría</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">Todas</option>
              {AUDIT_LOG_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {CATEGORY_LABELS[category] || category}
                </option>
              ))}
            </select>
          </div>
          <div className="audit-logs-field audit-logs-search">
            <label>Buscar</label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Mensaje, acción o categoría"
            />
          </div>
        </div>

        <div className="audit-logs-table">
          <div className="audit-logs-row audit-logs-head">
            <span>Fecha</span>
            <span>Nivel</span>
            <span>Categoría</span>
            <span>Acción</span>
            <span>Mensaje</span>
            <span>Usuario</span>
            <span></span>
          </div>

          {filteredLogs.length === 0 && (
            <div className="audit-logs-empty">No hay eventos para los filtros seleccionados</div>
          )}

          {filteredLogs.map((log) => {
            const createdAt = log?.createdAt?.toDate ? log.createdAt.toDate().toLocaleString() : '—'
            const userLabel = log?.user?.displayName || log?.user?.email || log?.user?.uid || 'Sistema'
            const expanded = expandedRows.has(log.id)
            return (
              <div key={log.id} className="audit-logs-row">
                <span>{createdAt}</span>
                <span className={`audit-logs-pill level-${log.level}`}>{LEVEL_LABELS[log.level] || log.level}</span>
                <span>{CATEGORY_LABELS[log.category] || log.category}</span>
                <span>{log.action || '—'}</span>
                <span className="audit-logs-message">{log.message || '—'}</span>
                <span>{userLabel}</span>
                <button
                  type="button"
                  className="audit-logs-toggle"
                  onClick={() => toggleExpanded(log.id)}
                >
                  {expanded ? 'Ocultar' : 'Detalles'}
                </button>
                {expanded && (
                  <div className="audit-logs-details">
                    <div>
                      <strong>Metadata</strong>
                      <pre>{JSON.stringify(log.metadata || {}, null, 2)}</pre>
                    </div>
                    <div>
                      <strong>Error</strong>
                      <pre>{JSON.stringify(log.error || {}, null, 2)}</pre>
                    </div>
                    <div>
                      <strong>Origen</strong>
                      <pre>{JSON.stringify(log.source || {}, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {notification && (
        <Toast
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={handleClearLogs}
        title="Borrar logs de auditoría"
        message="¿Estás seguro de borrar todos los logs? Esta acción no se puede deshacer."
        confirmText="Borrar"
        cancelText="Cancelar"
        variant="danger"
      />
    </div>
  )
}

export default AdminAuditLogs
